import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { twoWayAuthConfirmation, getUserByToken, twoWayAuthResend } from '../core/_requests';
import { showError, showSuccess } from '../../../../_mel/customPartials/helper/Alerts';
import { useIntl } from 'react-intl';
import { useAuth } from '../core/Auth';
import Cookies from 'universal-cookie';
import { onGetUserRoles } from '../../base-definitions/components/roles/core/_request';
import { Card } from 'react-bootstrap';

export function TwoWayAuth({ username, password }: { username: string; password: string }) {
  const intl = useIntl();
  const navigate = useNavigate();
  const { saveAuth, setCurrentUser } = useAuth();
  const [code, setCode] = useState<string[]>(Array(6).fill(''));
  const [loading, setLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState<boolean | undefined>(undefined);

  const handleCodeChange = (index: number, value: string) => {
    if (!/^\d*$/.test(value)) return; // Allow only digits
    const newCode = [...code];
    newCode[index] = value;

    if (value && index < code.length - 1) {
      // Move to the next input if value is entered
      const nextInput = document.getElementById(`digit-${index + 1}`);
      nextInput?.focus();
    }
    setCode(newCode);
  };

  const handleKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace') {
      if (!code[index] && index > 0) {
        // Move to the previous input on backspace if current is empty
        const prevInput = document.getElementById(`digit-${index - 1}`);
        prevInput?.focus();
      }
    }
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').slice(0, 6); // Limit to 6 characters
    if (!/^\d+$/.test(pastedData)) return; // Ensure all pasted characters are digits

    const newCode = Array(6).fill('');
    pastedData.split('').forEach((char, i) => {
      newCode[i] = char;
    });
    setCode(newCode);

    // Focus the last input filled
    const lastInput = document.getElementById(`digit-${pastedData.length - 1}`);
    lastInput?.focus();
  };

  const handleResendCode = async () => {
      setLoading(true);
      try {
        // Call your API endpoint to resend the authentication code
        const response = await twoWayAuthResend(
          username,
          password,
          intl.locale ?? "tr-TR");
        
        if (response.data.success && response.data.result.isLoginSuccess) {
          // Optionally, notify the user that the code was resent successfully
          showSuccess(intl.formatMessage({ id: 'AUTH.TWOWAYAUTH.CODE_RESENT' }));
        } else {
          let message =
            response.data?.result?.errorMessage ??
            response.data?.result?.message ??
            intl.formatMessage({ id: 'AUTH.TWOWAYAUTH.FAIL' });
          showError(message);
        }
      } catch (error) {
        let message =
          (error as any)?.response?.data?.error?.message ??
          intl.formatMessage({ id: 'AUTH.TWOWAYAUTH.FAIL' });
        showError(message);
      } finally {
        setLoading(false);
      }
    };

  const handleSubmit = async () => {
    const codeString = code.join('');
    if (codeString.length !== 6) {
      showError(intl.formatMessage({ id: 'AUTH.TWOWAYAUTH.ENTER_VALID_CODE' }));
      return;
    }

    setLoading(true);
    try {
      const response = await twoWayAuthConfirmation(
        username,
        password,
        codeString,
        intl.locale ?? "tr-TR"
      );
      
      if (response.data.success && response.data.result.isLoginSuccess) {
        setIsSuccess(true);
        setTimeout(async () => {
          let auth = response.data;
          saveAuth(auth);

          const cookies = new Cookies();
          cookies.set('enc_auth_token', auth.result.accessToken, {
            path: '/',
          });

          const { data: user } = await getUserByToken();
          const { data: permissions } = await onGetUserRoles();
          user.result.permissions = permissions.result;
          setCurrentUser(user);
        }, 0);
      } else {
        setIsSuccess(false);
        let message = response.data?.result?.errorMessage ?? response.data?.result?.message ?? intl.formatMessage({ id: 'AUTH.TWOWAYAUTH.FAIL' });
        showError(message);
      }
    } catch (error) {
      let message = (error as any)?.response?.data?.error?.message ?? intl.formatMessage({ id: 'AUTH.TWOWAYAUTH.FAIL' });
      showError(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="d-flex flex-column align-items-center justify-content-center h-100 ">
      <Card className="card shadow-sm p-5">
        <h3 className="text-center">{intl.formatMessage({ id: 'AUTH.TWOWAYAUTH.HEADER' })}</h3>
        <p className="text-muted text-center pt-3" style={{ width: '25rem', margin: '0 auto' }}>
          {intl.formatMessage({ id: 'AUTH.TWOWAYAUTH.DESCRIPTION' })}
        </p>

        <div
          className="d-flex justify-content-center gap-2 mb-2 py-5"
          onPaste={handlePaste}
        >
          {Array.from({ length: 6 }).map((_, index) => (
            <input
              key={index}
              id={`digit-${index}`}
              type="text"
              maxLength={1}
              value={code[index]}
              onChange={(e) => handleCodeChange(index, e.target.value)}
              onKeyDown={(e) => handleKeyDown(index, e)}
              className="form-control text-center"
              style={{ width: '3rem', height: '3rem', fontSize: '1.4rem' }}
            />
          ))}
        </div>
        
        <div className="pt-1 pb-4 text-center">
        {intl.formatMessage({ id: 'AUTH.TWOWAYAUTH.IFCODENOTSEND' })}{' '}
          <a
            href="#"
            onClick={handleResendCode}
            style={{ textDecoration: 'underline', color: 'blue', cursor: 'pointer' }}
          >
            {intl.formatMessage({ id: 'AUTH.TWOWAYAUTH.RESENDCODE' })}
          </a>
        </div>

        <button
          className="btn btn-primary w-100"
          onClick={handleSubmit}
          disabled={loading}
        >
          {loading ? intl.formatMessage({ id: 'AUTH.TWOWAYAUTH.LOADING' }) : intl.formatMessage({ id: 'AUTH.TWOWAYAUTH.SUBMIT' })}
        </button>

        {isSuccess === false && (
          <div className="alert alert-danger mt-3 text-center">
            {intl.formatMessage({ id: 'AUTH.TWOWAYAUTH.FAIL' })}
          </div>
        )}
      </Card>
    </div>
  );
}
